import React, { useCallback, useEffect, useMemo } from 'react';

import { format, parseISO, addDays } from 'date-fns';
import { HiDotsVertical } from 'react-icons/hi';
import { useHistory, useParams } from 'react-router-dom';

import { usePackages } from '~/hooks/packages';
import { useStudent } from '~/hooks/student';
import { currencyFormatter } from '~/utils/formatter';

import BillingProps from '../interface';

import {
  Container,
  CardLine,
  CardLineLabel,
  CardLineValue,
  CardStatus,
  CardStatusWrapper,
  CardStatusLabel,
  CardStatusValue,
  Options,
  CardContainer,
} from './styles';

interface Props {
  billing: BillingProps;
}

interface IParams {
  userId: string;
}

const BillingCard: React.FC<Props> = ({ billing }) => {
  const { userId } = useParams<IParams>();
  const { push } = useHistory();
  const { loadPackages, packages } = usePackages();
  const { studentData } = useStudent();

  const reportUrl = useMemo(() => {
    return `${process.env.REACT_APP_API_URL}/students/${studentData?.student?.id}/billing/${billing?.id}/pdf`;
  }, [studentData, billing]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const packageData = packages?.filter(
    (package_) => package_.id === billing.package?.id,
  )[0];

  const statusVariations: { [key: string]: string } = {
    pending: 'Pendente',
    paid: 'Paga',
    overdue: 'Vencida',
    canceled: 'Cancelada',
  };

  const handleNavigateToBilling = useCallback(() => {
    push(`/professional/students/${userId}/billings/${billing.id}`, billing);
  }, [push, userId, billing]);

  const handleNavigateToEditBilling = useCallback(() => {
    const financialStatusOptions = [
      { value: 'pending', label: 'Pendente' },
      { value: 'paid', label: 'Paga' },
      { value: 'overdue', label: 'Vencida' },
      { value: 'canceled', label: 'Cancelada' },
    ];

    const selectedPackage = {
      value: packageData.id,
      label: packageData.name,
      lealtad: packageData.lealtad,
      number_of_training_programs: packageData.number_of_training_programs,
    };

    const defaultBillingStatus = financialStatusOptions.filter(
      (status) => status.value === billing.status,
    )[0];

    const billingData = {
      ...billing,
      package: selectedPackage,
      status: defaultBillingStatus,
    };

    push(
      `/professional/students/${userId}/billings/${billing.id}/edit`,
      billingData,
    );
  }, [push, userId, billing, packageData]);

  return (
    <Container>
      <Options>
        <HiDotsVertical size={20} color="#000" />

        <div>
          <button onClick={handleNavigateToEditBilling} type="button">
            Editar
          </button>

          <div className="divider" />

          <button onClick={handleNavigateToBilling} type="button">
            Detalhes
          </button>
        </div>
      </Options>

      <CardContainer onClick={handleNavigateToBilling}>
        <CardLine>
          <CardLineLabel>ID:</CardLineLabel>
          <CardLineValue>{billing.id}</CardLineValue>
        </CardLine>

        <CardLine>
          <CardLineLabel>Pacote:</CardLineLabel>
          <CardLineValue>{billing.package?.name}</CardLineValue>
        </CardLine>

        <CardLine>
          <CardLineLabel>Programas de treino:</CardLineLabel>
          <CardLineValue>{billing.programs?.length}</CardLineValue>
        </CardLine>

        <CardLine>
          <CardLineLabel>Início:</CardLineLabel>
          <CardLineValue>
            {format(parseISO(billing.start_date ?? ''), 'dd/MM/yyyy')}
          </CardLineValue>
        </CardLine>

        <CardLine>
          <CardLineLabel>Término:</CardLineLabel>
          <CardLineValue>
            {format(parseISO(billing.end_date ?? ''), 'dd/MM/yyyy')}
          </CardLineValue>
        </CardLine>

        <CardLine>
          <CardLineLabel>Valor mensal:</CardLineLabel>
          <CardLineValue>
            {currencyFormatter(billing.monthly_amount ?? 0)}
          </CardLineValue>
        </CardLine>

        <CardLine>
          <CardLineLabel>Valor total:</CardLineLabel>
          <CardLineValue>
            {currencyFormatter(billing.total_amount ?? 0)}
          </CardLineValue>
        </CardLine>

        <CardStatus status={statusVariations[billing.status ?? '']}>
          <CardStatusWrapper>
            <CardStatusLabel>Status:</CardStatusLabel>
            <CardStatusValue>
              {statusVariations[billing.status ?? '']}
            </CardStatusValue>
          </CardStatusWrapper>
        </CardStatus>
      </CardContainer>
    </Container>
  );
};

export default BillingCard;
